import { ErrorText, routes } from '~/constants';
import { useOverlayContext, useIoContext, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { useOpenCopyLinkModal } from './useOpenCopyLinkModal/useOpenCopyLinkModal';
export function useCopyProposalLink() {
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { api, logBI } = useStoreActions();
    const { openCopyLinkForProposal } = useOpenCopyLinkModal();
    const { copyToClipboard } = useIoContext();
    async function getAgreementNotificationLink(agreementId) {
        try {
            const notificationId = await api.relationship.getSingleUseApprovalLink(agreementId);
            return "https://".concat(window.location.host).concat(routes.notification(notificationId));
        } catch (err) {
            handleGetLinkError(err);
            throw err;
        }
    }
    async function getAgreementNotificationLinks(agreementId) {
        try {
            const response = await api.relationship.getProposalLinks(agreementId);
            return response;
        } catch (err) {
            handleGetLinkError(err);
            throw err;
        }
    }
    function handleGetLinkError(err) {
        const e = new ErrorResponse(err);
        const message = e.hasServerError(ServerError.RelationshipAlreadyApproved) ? 'Failed to generate link, the proposal was already approved' : commonTexts.toasts.failedToGenerateLink();
        overlayContext.showErrorToast(message, 'agreement-link-error-toast');
    }
    async function copyNotificationUrl(notificationUrl) {
        try {
            await copyToClipboard(notificationUrl);
            overlayContext.showSuccessToast(commonTexts.toasts.linkCopiedToClipboard(), 'copy-link-success');
        } catch (e) {
            // e.g. fails if the document loses focus.
            overlayContext.showErrorToast(commonTexts.toasts.failedToCopyLinkToClipboard());
        }
    }
    async function copyAgreementLink(param) {
        let { agreementId, vendorId, customerId, progressStatus, withGlobalLoader } = param;
        logBI(10589, {
            vendorId,
            clientId: customerId,
            agreementStatus: progressStatus
        });
        const loaderName = 'copyAgreementLink';
        withGlobalLoader && overlayContext.startLoader(loaderName);
        const response = await handleRequest(getAgreementNotificationLinks(agreementId));
        withGlobalLoader && overlayContext.stopLoader(loaderName);
        if (!response.success) {
            overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain);
            return;
        }
        const key = 'recipients';
        await openCopyLinkForProposal({
            [key]: response.data,
            testId: 'copy-direct-link-copy'
        });
    }
    return {
        getAgreementNotificationLink,
        getAgreementNotificationLinks,
        copyAgreementLink,
        copyNotificationUrl
    };
}
